import mitt, { Emitter } from 'mitt';
import {
  SuprSendOptions,
  Dictionary,
  EmitterEvents,
  AuthenticateOptions,
  ERROR_TYPE,
  RESPONSE_STATUS,
  ApiResponse,
  ClientUserAgentConfig,
  TrackOptions,
  ChangeTenantOptions,
  PushTokenAction,
} from './interface';
import ApiClient from './api';
import {
  uuid,
  epochMs,
  getResponsePayload,
  getLocalStorageData,
  setLocalStorageData,
  removeLocalStorageData,
  buildUserAgent,
  buildClientUserAgent,
} from './utils';
import User from './user';
import WebPush, { SUPRSEND_ENDPOINT_KEY } from './webpush';
import FeedsFactory from './feed';

const DEFAULT_HOST = 'https://hub.suprsend.com';
const DEFAULT_SW_FILENAME = 'serviceworker.js';
const AUTHENTICATED_DISTINCT_ID = 'ss_distinct_id';
const PUSH_TOKEN_ACTIONS: PushTokenAction[] = ['none', 'copy', 'move'];

export default class SuprSend {
  public host: string;
  public publicApiKey: string;
  public distinctId: unknown;
  public userToken?: string;
  public tenantId?: string;
  public vapidKey: string;
  public swFileName: string;
  public clientUserAgent: ClientUserAgentConfig;
  public userAgent: string;
  private apiClient: ApiClient | null = null;
  public authenticateOptions?: Omit<AuthenticateOptions, 'tenantId'>;

  readonly user = new User(this);
  readonly webpush = new WebPush(this);
  readonly feeds = new FeedsFactory(this);
  readonly emitter: Emitter<EmitterEvents> = mitt();

  constructor(publicApiKey: string, options?: SuprSendOptions) {
    if (!publicApiKey) {
      throw new Error('[SuprSend]: publicApiKey is missing');
    }

    this.publicApiKey = publicApiKey;
    this.host = options?.host || DEFAULT_HOST;
    this.vapidKey = options?.vapidKey || '';
    this.swFileName = options?.swFileName || DEFAULT_SW_FILENAME;
    this.clientUserAgent = buildClientUserAgent(
      options?.appInfo,
      options?.clientUserAgent
    );
    this.userAgent = buildUserAgent(this.clientUserAgent);
  }

  client() {
    if (!this.distinctId) {
      console.warn(
        '[SuprSend]: distinctId is missing. User should be authenticated'
      );
    }

    if (!this.apiClient) {
      this.apiClient = new ApiClient(this);
    }

    return this.apiClient;
  }

  eventApi(payload: Dictionary) {
    return this.client().request({
      url: `${this.host}/v2/event`,
      payload,
      type: 'post',
    });
  }

  /**
   *  Used to authenticate user. Usually called just after successful login and on reload of loggedin route to re-authenticate loggedin user.
   *  In production env's userToken is mandatory for security purposes.
   */
  async identify(
    distinctId: unknown,
    userToken?: string,
    options?: AuthenticateOptions
  ) {
    if (!distinctId) {
      return getResponsePayload({
        status: RESPONSE_STATUS.ERROR,
        errorType: ERROR_TYPE.VALIDATION_ERROR,
        errorMessage: 'distinctId is missing',
      });
    }

    // other user already present
    if (this.apiClient && this.distinctId && this.distinctId !== distinctId) {
      return getResponsePayload({
        status: RESPONSE_STATUS.ERROR,
        errorType: ERROR_TYPE.VALIDATION_ERROR,
        errorMessage:
          'User already loggedin, reset current user to login new user',
      });
    }

    if (options?.tenantId) {
      this.tenantId = options.tenantId;
    }

    // updating usertoken for existing user
    if (
      this.apiClient &&
      this.distinctId === distinctId &&
      this.userToken !== userToken
    ) {
      this.userToken = userToken;
      this.apiClient = new ApiClient(this);
      if (options) {
        const { tenantId: _tenantId, ...authOptions } = options;
        this.authenticateOptions = {
          ...this.authenticateOptions,
          ...authOptions,
        };
      }
      return getResponsePayload({ status: RESPONSE_STATUS.SUCCESS });
    }

    // ignore more than one identify call
    if (this.distinctId && this.apiClient) {
      return getResponsePayload({ status: RESPONSE_STATUS.SUCCESS });
    }

    this.distinctId = distinctId;
    this.userToken = userToken;
    this.apiClient = new ApiClient(this);
    const { tenantId: _tenantId, ...authOptions } = options || {};
    this.authenticateOptions = authOptions;
    const authenticatedDistinctId = getLocalStorageData(
      AUTHENTICATED_DISTINCT_ID
    );

    // already loggedin
    if (authenticatedDistinctId == this.distinctId) {
      this.webpush.updatePushSubscription();
      return getResponsePayload({ status: RESPONSE_STATUS.SUCCESS });
    }

    let resp: ApiResponse;
    const createUser = options?.createUser !== false;
    // first time login
    if (createUser) {
      resp = await this.eventApi({
        event: '$identify',
        $insert_id: uuid(),
        $time: epochMs(),
        tenant_id: this.tenantId || null,
        properties: {
          $identified_id: distinctId,
        },
      });
    } else {
      resp = { status: RESPONSE_STATUS.SUCCESS };
    }

    if (resp.status === RESPONSE_STATUS.SUCCESS) {
      // store user so that other method calls dont need api calls
      this.webpush.updatePushSubscription();
      setLocalStorageData(AUTHENTICATED_DISTINCT_ID, this.distinctId as string);
    } else {
      // reset user data so that user can retry
      this.reset({ unsubscribePush: false });
    }
    return resp;
  }

  /**
   * Check's if SuprSend instance is authenticated. To check if userToken is also present pass true.
   */
  isIdentified(checkUserToken?: boolean) {
    return checkUserToken
      ? !!(this.userToken && this.distinctId)
      : !!this.distinctId;
  }

  /**
   * Used to switch active tenant of identified user. Already running feed instances and preferences
   * keep the tenant they were initialized with. Pass pushTokenAction in options to control the
   * webpush subscription: 'none' leaves it attached to the current tenant, 'copy' attaches it to the
   * new tenant as well, 'move' detaches it from the current tenant and attaches it to the new tenant.
   */
  async changeTenant(tenantId: string | null, options?: ChangeTenantOptions) {
    if (tenantId !== null && (!tenantId || typeof tenantId !== 'string')) {
      return getResponsePayload({
        status: RESPONSE_STATUS.ERROR,
        errorType: ERROR_TYPE.VALIDATION_ERROR,
        errorMessage: 'tenantId is missing or invalid',
      });
    }

    const pushTokenAction = options?.pushTokenAction ?? 'none';
    if (!PUSH_TOKEN_ACTIONS.includes(pushTokenAction)) {
      return getResponsePayload({
        status: RESPONSE_STATUS.ERROR,
        errorType: ERROR_TYPE.VALIDATION_ERROR,
        errorMessage: "pushTokenAction must be one of 'none', 'copy' or 'move'",
      });
    }

    if (!this.isIdentified()) {
      console.warn(
        '[SuprSend]: changeTenant called before identify. Tenant will apply once user is identified'
      );
    }

    const oldTenantId = this.tenantId;
    const newTenantId = tenantId ?? undefined;
    const attachPush =
      pushTokenAction !== 'none' &&
      this.isIdentified() &&
      oldTenantId !== newTenantId &&
      (await this.webpush.pushSubscribed());

    if (attachPush && pushTokenAction === 'move') {
      const removeResp = await this.webpush.removePushSubscription();
      if (removeResp?.status === RESPONSE_STATUS.ERROR) {
        return removeResp;
      }
      removeLocalStorageData(SUPRSEND_ENDPOINT_KEY);
    }

    this.tenantId = newTenantId;

    if (attachPush) {
      const updateResp = await this.webpush.updatePushSubscription();
      if (updateResp?.status === RESPONSE_STATUS.ERROR) {
        this.tenantId = oldTenantId;
        if (pushTokenAction === 'move') {
          await this.webpush.updatePushSubscription();
        }
        return updateResp;
      }
    }

    return getResponsePayload({ status: RESPONSE_STATUS.SUCCESS });
  }

  /**
   *  Used to trigger events to suprsend.
   */
  async track(event: string, properties?: Dictionary, options?: TrackOptions) {
    let propertiesObj: Dictionary = {};

    if (!event) {
      return getResponsePayload({
        status: RESPONSE_STATUS.ERROR,
        errorType: ERROR_TYPE.VALIDATION_ERROR,
        errorMessage: 'event name is missing',
      });
    }
    // TODO: add check to validate special keys
    if (typeof properties === 'object') {
      propertiesObj = { ...propertiesObj, ...properties };
    }

    return this.eventApi({
      event: String(event),
      $insert_id: uuid(),
      $time: epochMs(),
      distinct_id: this.distinctId,
      tenant_id: options?.tenantId || this.tenantId || null,
      properties: propertiesObj,
    });
  }

  /**
   * Clears user related data attached to SuprSend instance. Usually called during logout.
   */
  async reset(options?: { unsubscribePush?: boolean }) {
    const unsubscribePush = !(options?.unsubscribePush === false); // defaults to true

    if (unsubscribePush) {
      await this.webpush?.removePushSubscription();
      removeLocalStorageData(SUPRSEND_ENDPOINT_KEY);
    }

    this.apiClient = null;
    this.distinctId = null;
    this.userToken = '';
    this.tenantId = undefined;
    // removeLocalStorageData(AUTHENTICATED_DISTINCT_ID);

    this.user.preferences.reset();

    if (this.feeds.feedInstances?.length > 0) {
      this.feeds.removeAll();
    }
    return getResponsePayload({ status: RESPONSE_STATUS.SUCCESS });
  }
}
